import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [isImpersonating, setIsImpersonating] = useState(!!localStorage.getItem('adminToken'));

  // Check for OAuth redirect with token in URL (subdomain redirect support)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const oauthToken = urlParams.get('token');
    const isOAuthRedirect = urlParams.get('oauth_redirect');
    
    if (oauthToken && isOAuthRedirect === 'true') {
      // Store token from OAuth redirect
      localStorage.setItem('token', oauthToken);
      setToken(oauthToken);
      
      // Clean up URL params
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, document.title, cleanUrl);
    }
  }, []);

  useEffect(() => {
    if (token) {
      fetchCurrentUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchCurrentUser = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  // Function to refresh user data (call after profile updates)
  const refreshUser = async () => {
    if (token) {
      try {
        const response = await axios.get(`${API_URL}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUser(response.data);
        return response.data;
      } catch (error) {
        console.error('Failed to refresh user:', error);
      }
    }
    return null;
  };

  const login = async (email, password, companyId = null) => {
    const loginData = { email, password };
    if (companyId) {
      loginData.company_id = companyId;
    }
    const response = await axios.post(`${API_URL}/api/auth/login`, loginData);
    const { access_token, user } = response.data;
    setToken(access_token);
    setUser(user);
    localStorage.setItem('token', access_token);
    // Clear any impersonation state on fresh login
    localStorage.removeItem('adminToken');
    setIsImpersonating(false);
    
    // Auto-apply user's saved language preference
    if (user.preferred_language && user.preferred_language !== 'en') {
      applyLanguagePreference(user.preferred_language);
    }
    
    return user;
  };

  // Apply language preference using Google Translate
  const applyLanguagePreference = (langCode) => {
    try {
      // Set the Google Translate cookie
      // Format: googtrans=/en/[target_language]
      const domain = window.location.hostname;
      document.cookie = `googtrans=/en/${langCode}; path=/; domain=${domain}`;
      document.cookie = `googtrans=/en/${langCode}; path=/`;
      
      // Also try to trigger Google Translate widget if it's loaded
      const selectElement = document.querySelector('.goog-te-combo');
      if (selectElement) {
        selectElement.value = langCode;
        selectElement.dispatchEvent(new Event('change', { bubbles: true }));
      }
    } catch (error) {
      console.log('Could not apply language preference:', error);
    }
  };

  // Login as another user (for admin impersonation)
  const loginWithToken = (accessToken, userData, saveAdminToken = false) => {
    if (saveAdminToken && token && user?.role === 'admin') {
      // Save the admin token before impersonating
      localStorage.setItem('adminToken', token);
      setIsImpersonating(true);
    }
    setToken(accessToken);
    setUser(userData);
    localStorage.setItem('token', accessToken);
  };

  // Return to admin account from impersonation
  const returnToAdmin = async () => {
    const adminToken = localStorage.getItem('adminToken');
    if (adminToken) {
      setToken(adminToken);
      localStorage.setItem('token', adminToken);
      localStorage.removeItem('adminToken');
      setIsImpersonating(false);
      // Fetch admin user data
      try {
        const response = await axios.get(`${API_URL}/api/auth/me`, {
          headers: { Authorization: `Bearer ${adminToken}` }
        });
        setUser(response.data);
        return true;
      } catch (error) {
        console.error('Failed to return to admin:', error);
        return false;
      }
    }
    return false;
  };

  const register = async (email, password) => {
    const response = await axios.post(`${API_URL}/api/auth/register`, { email, password });
    const { access_token, user } = response.data;
    setToken(access_token);
    setUser(user);
    localStorage.setItem('token', access_token);
    return user;
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('adminToken');
    setIsImpersonating(false);
  };

  const isAdmin = () => {
    return user && ['admin', 'main_admin', 'company_admin'].includes(user.role);
  };

  // Check if user is main admin (full platform access)
  const isMainAdmin = () => {
    return user && ['admin', 'main_admin'].includes(user.role);
  };

  // Check if user is company admin (restricted to their company)
  const isCompanyAdmin = () => {
    return user && user.role === 'company_admin';
  };

  return (
    <AuthContext.Provider value={{ 
      user, token, login, loginWithToken, register, logout, loading, isAdmin, isMainAdmin, isCompanyAdmin, refreshUser,
      isImpersonating, returnToAdmin 
    }}>
      {children}
    </AuthContext.Provider>
  );
};
